<template>
  <v-card class="pt-6 pb-3 px-8 ma-0" :class="{ 'rounded-t-0': isEditMode }">
    <TemplateLabel v-if="isTemplate" transition="fade-transition" />

    <v-card-title
      data-testid="routineForm-text-cardTitle"
      class="pa-0 mb-4 d-flex justify-space-between align-top"
      :class="{ 'mb-6': isEditMode }"
    >
      <p class="ma-0 pa-0 h6 blue-grey--text text--darken-3">
        {{ title }}
      </p>
      <p class="ma-0 pa-0 caption blue-grey--text">
        {{ $t('common.required_fields') }}*
      </p>
    </v-card-title>

    <v-card-subtitle
      data-testid="routineForm-text-cardSubtitle"
      class="pa-0 ma-0 mb-6 blue-grey--text text--darken-1 body-1"
      v-if="!routineDefinitionId"
      v-html="
        $sanitize($t('routines.routine_form_subtitle'), {
          allowedTags: ['a', 'b', 'br'],
        })
      "
    />

    <v-row class="my-0">
      <v-col cols="12">
        <v-text-field
          data-testid="routineForm-input-name"
          data-private
          dense
          outlined
          class="mb-2"
          required
          ref="1routineDefinitionName"
          :loading="isLoading"
          :maxlength="$constants.routines.definition.titleMaxLength"
          :disabled="isSaving || isLoading"
          :label="`${$t('common.name')}*`"
          :rules="rules"
          :value="form.name"
          @change="data => $emit('updateForm', data, 'name')"
        />

        <v-textarea
          data-testid="routineForm-input-description"
          auto-grow
          data-private
          dense
          no-resize
          outlined
          required
          ref="2routineDefinitionDescription"
          style="white-space: pre-wrap; word-break: break-word;"
          :disabled="isSaving || isLoading"
          :loading="isLoading"
          :label="`${$t('common.description')}*`"
          :rules="rules"
          :value="form.description"
          @change="data => $emit('updateForm', data, 'description')"
        />

        <v-col cols="3" class="mb-6">
          <div class="d-flex align-center ma-0 pa-0">
            <v-switch
              data-testid="routineForm-switch-isActive"
              hide-details
              class="ma-0 pa-0"
              :loading="isLoading"
              :disabled="isSaving || isLoading"
              :input-value="form.isActive"
              @change="data => $emit('updateForm', Boolean(data), 'isActive')"
            />
            <p class="ma-0 pa-0 me-4 blue-grey--text text--darken-1 body-1">
              {{ $t('common.active') }}
            </p>
          </div>
        </v-col>
        <v-divider class="mt-3 mb-6" />
        <div
          ref="routineDefinitionAssignees"
          :valid="!showAssigneesError"
          data-tourid="routineDefinition-form-assignees-section"
        >
          <div class="d-flex align-center mb-6">
            <p
              class="ma-0 pa-0 h6 font-weight-medium blue-grey--text text--darken-3"
            >
              {{ $t('common.assignee_other') }}*
            </p>
            <div v-if="showAssigneesError" class="d-flex align-center ms-2">
              <v-icon color="red" size="16">mdi-alert-circle-outline</v-icon>
              <p class="ma-0 pa-0 red--text ms-1 caption">
                {{ $t('routines.assignees_error') }}
              </p>
            </div>
          </div>

          <Alert
            v-if="hasMaxAssignees"
            class="py-4"
            :vuetifyProps="{
              color: 'warning',
              icon: 'mdi-account-alert-outline',
            }"
            :text="$t('routines.maximum_single_assignees')"
          />

          <v-row class="my-0" v-if="isUserOrgAdmin">
            <v-col
              sm="12"
              xs="12"
              md="12"
              lg="6"
              cols="auto"
              :class="{ 'mb-2': $vuetify.breakpoint.mdAndDown }"
            >
              <RoutineExtentCard
                icon="mdi-account-cog"
                :isActive="!form.isGloballyAssigned"
                :title="$t('routines.extent_card_custom_title')"
                :description="$t('routines.extent_card_custom_description')"
                @click="$emit('updateForm', false, 'isGloballyAssigned')"
              />
            </v-col>
            <v-col sm="12" xs="12" md="12" lg="6" cols="auto">
              <RoutineExtentCard
                icon="mdi-earth"
                :isActive="form.isGloballyAssigned"
                :title="$t('routines.extent_card_global_title')"
                :description="$t('routines.extent_card_global_description')"
                @click="$emit('updateForm', true, 'isGloballyAssigned')"
              />
            </v-col>
          </v-row>

          <div v-show="!form.isGloballyAssigned" class="mt-6">
            <div class="d-flex align-center mb-4">
              <p
                class="body-1 mb-0 font-weight-medium blue-grey--text text--darken-2"
              >
                {{ $t('common.site_other') }}
                {{
                  form.assignedSites.length
                    ? `(${form.assignedSites.length})`
                    : ''
                }}
              </p>

              <Button
                v-if="form.assignedSites.length"
                class="ms-2"
                color="primary"
                plain
                size="small"
                :text="$t('common.clear_all')"
                @click="handleChangeAssignedSites([])"
              />
            </div>

            <MultipleSiteSelector
              class="mb-6"
              :disabled="isSaving || hasMaxAssignees"
              :maxSites="50"
              :value="form.assignedSites"
              :zIndex="1"
              @change="handleChangeAssignedSites"
            />

            <div class="d-flex align-center mb-4">
              <p
                class="body-1 mb-0 font-weight-medium blue-grey--text text--darken-2"
              >
                {{ $t('common.team_other') }}
                {{
                  form.assignedTeams.length
                    ? `(${form.assignedTeams.length})`
                    : ''
                }}
              </p>

              <Button
                v-if="form.assignedTeams.length"
                class="ms-2"
                color="primary"
                plain
                size="small"
                :text="$t('common.clear_all')"
                @click="handleChangeAssignedTeams([])"
              />
            </div>

            <MultipleTeamSelector
              class="mb-6"
              :disabled="isSaving || hasMaxAssignees"
              :value="form.assignedTeams"
              :zIndex="1"
              @change="handleChangeAssignedTeams"
            />

            <div class="d-flex align-center mb-4">
              <p
                class="body-1 mb-0 font-weight-medium blue-grey--text text--darken-2"
              >
                {{ $t('common.users.user_other') }}
                {{ form.assignees.length ? `(${form.assignees.length})` : '' }}
              </p>

              <Button
                v-if="form.assignees.length"
                class="ms-2"
                color="primary"
                plain
                size="small"
                :text="$t('common.clear_all')"
                @click="handleChangeAssignees([])"
              />
            </div>

            <AssigneesSelector
              hideLabel
              multiple
              showAdvancedOption
              :maxSelectableAccounts="100"
              :selectedAccountIds="form.assignees"
              :disabled="isSaving"
              :zIndex="1"
              :buttonCopy="$t('routines.add_user_other')"
              @change="handleChangeAssignees"
            />
          </div>
        </div>
      </v-col>

      <v-col
        cols="12"
        data-tourid="routineDefinition-form-configuration-section"
      >
        <p
          class="text-h6 mt-4 mb-4 font-weight-medium blue-grey--text text--darken-3"
        >
          {{ $t('routines.configuration') }}
        </p>
        <v-select
          data-testid="routineForm-selector-frequency"
          dense
          outlined
          :value="form.frequency"
          :disabled="isSaving || isLoading"
          :loading="isLoading"
          :items="availableFrequencies"
          :label="$t('routines.frequency_label')"
          @change="data => $emit('updateForm', data, 'frequency')"
        />

        <v-select
          data-testid="routineForm-selector-routineType"
          dense
          outlined
          :value="form.type"
          :disabled="isSaving || isLoading"
          :loading="isLoading"
          :items="availableTypes"
          :label="$t('routines.routine_type')"
          @change="data => $emit('updateForm', data, 'type')"
        />

        <div class="d-flex align-center">
          <TagsSelector
            data-testid="routineForm-selector-availableTagsForExecutions"
            onlyGlobal
            :label="$t('routines.available_tags_for_executions')"
            :siteId="siteId"
            :value="form.presetTags"
            @change="handlePresetTagsChange"
          />
        </div>

        <div class="d-flex align-center mt-6">
          <TagsSelector
            data-testid="routineForm-selector-availableTagsForFindings"
            :label="$t('routines.available_tags_for_findings')"
            :siteId="siteId"
            :value="form.availableTagsForFindings"
            @change="handleAvailableTagsForFindingsChange"
          />
        </div>

        <div class="d-flex align-start mt-4 mb-2">
          <v-switch
            data-testid="routineForm-switch-requireFindingTags"
            class="ma-0 pa-0"
            hide-details
            :disabled="isSaving || isLoading"
            :input-value="form.requireFindingTags"
            @change="
              data => $emit('updateForm', Boolean(data), 'requireFindingTags')
            "
          />

          <div class="d-flex flex-column">
            <p class="ma-0 pa-0 body-1 blue-grey--text text--darken-1">
              {{ $t('routines.require_finding_tags') }}
            </p>

            <span class="caption blue-grey--text mt-1">
              {{ $t('routines.available_tags_for_findings_description') }}
            </span>
          </div>
        </div>

        <div class="mt-6">
          <CategoriesSelector
            data-private
            :disabled="isSaving || isLoading"
            :siteIds="siteIds"
            :value="form.categories"
            @input="data => $emit('updateForm', data, 'categories')"
          />
        </div>

        <div class="mt-6">
          <SituationTypesSelector
            :value="form.presetSituationTypes"
            @input="data => $emit('updateForm', data, 'presetSituationTypes')"
          />
        </div>
      </v-col>

      <v-col cols="12" class="mt-12 d-flex flex-column me-0 pe-2">
        <div class="d-flex align-start">
          <v-switch
            data-testid="routineForm-switch-isTemplate"
            hide-details
            class="me-2 my-0 mr-0 pa-0"
            :input-value="form.isGlobalTemplate"
            :loading="isLoading"
            :disabled="isSaving || isLoading"
            @change="
              data => $emit('updateForm', Boolean(data), 'isGlobalTemplate')
            "
          />

          <div class="d-flex flex-column">
            <p
              data-testid="routine-form-isTemplate"
              class="blue-grey--text text--darken-1 body-1 ma-0 pa-0 text-no-wrap"
            >
              {{ $t('routines.routine_template') }}
            </p>

            <span class="caption blue-grey--text mt-1">
              {{ $t('routines.routine_template_description') }}
            </span>
          </div>
        </div>
      </v-col>

      <v-divider class="my-6 mx-3" />

      <v-col
        cols="12"
        data-tourid="routineDefinition-form-collaborators-section"
      >
        <p class="h6 font-weight-medium blue-grey--text text--darken-3 mb-6">
          {{ $t('common.collaborator_other') }}
        </p>
        <p class="ma-0 pa-0 blue-grey--text text--darken-1 body-1 mb-6">
          {{ $t('routines.collaborators_description') }}
        </p>
        <AssigneesSelector
          multiple
          showAdvancedOption
          hideLabel
          :blacklist="blackList"
          :filters="{
            roles: [
              'isAdmin',
              'isOrganizationAdmin',
              'isSiteAdmin',
              'isRoutineManager',
            ],
          }"
          :maxSelectableAccounts="100"
          :buttonText="$t('routines.add_collaborators')"
          :selectedAccountIds="form.collaborators"
          :disabled="isSaving || !canEditCollaborators"
          @change="handleChangeCollaborators"
        />
      </v-col>
    </v-row>
    <v-card-actions class="pa-0 mb-0 pt-4 buttons-container d-flex justify-end">
      <div v-if="isEditMode" class="d-flex justify-end py-3">
        <Button
          plain
          color="grey"
          class="me-4"
          data-testid="routineForm-button-cancel"
          :disabled="isSaving || isLoading"
          :text="$t('common.button.cancel')"
          @click="$emit('handleCancel')"
        />

        <Button
          color="primary"
          data-testid="routineForm-button-save"
          :loading="isSaving"
          :disabled="isSaving || isLoading"
          :text="$t('common.button.save')"
          @click="$emit('handleSave', 'setup')"
        />
      </div>

      <div v-else class="d-flex justify-end py-3">
        <Button
          plain
          color="grey"
          class="me-6"
          data-testid="routineForm-button-cancel"
          :disabled="isSaving || isLoading"
          :text="$t('common.button.cancel')"
          @click="$emit('handleCancel')"
        />
        <Button
          plain
          iconPosition="right"
          color="primary"
          data-testid="routineForm-button-next"
          icon="mdi-chevron-right"
          :loading="isSaving"
          :disabled="isSaving || isLoading"
          :text="$t('common.button.next')"
          @click.prevent="$emit('goToChecklistStep')"
        />
      </div>
    </v-card-actions>
  </v-card>
</template>

<script>
import _ from 'lodash'
import { mapGetters } from 'vuex'
import access from '@/lib/access'
import getEntityId from '@/lib/getEntityId'
import routineDefinitionPermissions from '@/mixins/routineDefinitionPermissions.vue'

import AssigneesSelector from '@/components/AssigneesSelector/AssigneesSelector.vue'
import CategoriesSelector from '@/components/CategoriesSelector.vue'
import TagsSelector from '@/components/TagsSelector/TagsSelector.vue'
import SituationTypesSelector from '@/components/SituationTypesSelector.vue'
import TemplateLabel from '@/components/TemplateLabel.vue'
import Button from '@/components/UI/Button'
import MultipleTeamSelector from '@/components/MultipleTeamSelector.vue'
import MultipleSiteSelector from '@/components/MultipleSiteSelector.vue'
import RoutineExtentCard from './RoutineExtentCard.vue'
import Alert from '@/components/Alert.vue'

const STEPS = {
  ROUTINE: 1,
  CHECKLIST: 2,
  CONFIRMATION: 3,
}

export default {
  name: 'FormStep',

  mixins: [routineDefinitionPermissions],

  components: {
    CategoriesSelector,
    AssigneesSelector,
    TagsSelector,
    SituationTypesSelector,
    TemplateLabel,
    Button,
    MultipleTeamSelector,
    MultipleSiteSelector,
    RoutineExtentCard,
    Alert,
  },

  props: {
    form: {
      type: Object,
      required: true,
    },
    isLoading: Boolean,
    isSaving: Boolean,
    title: String,
    routineDefinitionId: String,
    isEditMode: Boolean,
    currentStep: Number,
    isTemplate: Boolean,
    showAssigneesError: Boolean,
  },

  data() {
    return {
      STEPS,
      rules: [
        val =>
          !!val?.trim() || this.$t('validation.form.this_field_is_required'),
      ],
      availableFrequencies: this.$constants.routines.frequencies.map(
        frequency => ({
          value: frequency.value,
          text: this.$t(frequency.name),
        })
      ),
      availableTypes: _.chain(this.$constants.routines.types)
        .map(type => ({
          value: type.value,
          text: this.$t(type.name),
        }))
        .orderBy('text', 'asc')
        .value(),
    }
  },

  mounted() {
    this.$refs['1routineDefinitionName'].focus()
  },

  computed: {
    ...mapGetters(['account']),

    canEditCollaborators() {
      if (!this.isEditMode) return true

      return this.getRoutineDefinitionPermissions({
        routineDefinition: this.form,
      }).manageCollaborators
    },

    siteIds() {
      return this.account?.site ? [this.account?.site?._id] : []
    },

    siteId() {
      return this.account?.site?._id ?? this.account?.site
    },

    isUserOrgAdmin() {
      return access.isOrganizationAdmin(this.account)
    },

    hasMaxAssignees() {
      return this.form?.assignees?.length > 100
    },

    blackList() {
      return this.isEditMode
        ? [getEntityId(this.form?.createdBy)]
        : [getEntityId(this.account)]
    },
  },

  methods: {
    handlePresetTagsChange(value) {
      this.$emit('updateForm', value.tags, 'presetTags')
    },

    handleAvailableTagsForFindingsChange(value) {
      this.$emit('updateForm', value.tags, 'availableTagsForFindings')
    },

    handleChangeAssignees(assignees) {
      this.$emit('updateForm', assignees, 'assignees')
    },

    handleChangeCollaborators(collaborators) {
      this.$emit('updateForm', collaborators, 'collaborators')
    },

    handleChangeAssignedSites(assignedSites) {
      this.$emit('updateForm', assignedSites, 'assignedSites')
    },
    handleChangeAssignedTeams(assignedTeams) {
      this.$emit('updateForm', assignedTeams, 'assignedTeams')
    },
  },
}
</script>
<style lang="scss" scoped>
.buttons-container {
  position: sticky;
  bottom: 0;
  background: linear-gradient(to bottom, transparent 10%, white 100%);
}
</style>
